import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { StoreKeys } from '@/configs/StoreKeys';
import {
	ComponentRelation,
	ComponentRelationCondition,
	UUID,
} from '@/types/types';
import type { RootState } from '@/store';
import { AttributeValue } from '@/api/AttributeValueApi';
import { ProductModifier } from '@/api/ComponentApi';
import { ComponentPriceRule, PriceRuleType } from '@/api/ProductTypeApi';

const findAttributeValue = (
	state: ComponentFormState,
	attributeId: UUID,
	valueId: UUID
) => {
	const attribute = state.data.componentAttributes.find(
		(attr) => attr.id === attributeId
	);
	return attribute?.componentAttributeValues?.find((val) => val.id === valueId);
};

export interface FormComponentAttribute {
	id: UUID;
	attribute: { id: UUID } | null;
	listOrder: number;
	componentAttributeValues: FormComponentAttributeValue[];
	relations: ComponentRelation[];
	required?: boolean;
}

export interface FormComponentAttributeValue {
	id: UUID;
	attributeValue?: Partial<AttributeValue>;
	productModifiers: ProductModifier[];
	relations: ComponentRelation[];
	priceRule: ComponentPriceRule | null;
}

export interface CreateComponentForm {
	name: string;
	displayName?: string;
	componentAttributes: FormComponentAttribute[];
}

interface ComponentFormState {
	data: CreateComponentForm & { id?: UUID };
}

const initialState: ComponentFormState = {
	data: {
		name: '',
		displayName: '',
		componentAttributes: [],
	},
};

export const componentFormSlice = createSlice({
	name: StoreKeys.componentForm,
	initialState,
	reducers: {
		initializeForm: (
			state,
			action: PayloadAction<CreateComponentForm & { id?: UUID }>
		) => {
			const formData = JSON.parse(JSON.stringify(action.payload));
			formData.componentAttributes = (formData.componentAttributes || []).map(
				(attr: any) => ({
					...attr,
					id: attr.id || crypto.randomUUID(),
					relations: attr.relations || [],
					componentAttributeValues:
						(attr.componentAttributeValues || []).map((val: any) => ({
							...val,
							id: val.id || crypto.randomUUID(),
							relations: val.relations || [],
							productModifiers: val.productModifiers || [],
						})) || [],
				})
			);
			state.data = formData;
		},
		resetForm: () => initialState,
		setComponentName: (state, action: PayloadAction<string>) => {
			state.data.name = action.payload;
		},
		setComponentDisplayName: (state, action: PayloadAction<string>) => {
			state.data.displayName = action.payload;
		},
		addAttribute: (state) => {
			const newAttribute: FormComponentAttribute = {
				id: crypto.randomUUID(),
				attribute: null,
				listOrder: 100,
				componentAttributeValues: [],
				relations: [],
			};
			state.data.componentAttributes.push(newAttribute);
		},
		removeAttribute: (state, action: PayloadAction<UUID>) => {
			state.data.componentAttributes = state.data.componentAttributes.filter(
				(attr) => attr.id !== action.payload
			);
		},
		updateAttribute: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				attribute: { id: UUID } | null;
				required?: boolean;
			}>
		) => {
			const { attributeId, attribute } = action.payload;
			const targetAttribute = state.data.componentAttributes.find(
				(attr) => attr.id === attributeId
			);
			if (targetAttribute) {
				targetAttribute.attribute = attribute;
				targetAttribute.componentAttributeValues = [];
			}
		},
		updateAttributePart: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				required?: boolean;
				listOrder?: number;
			}>
		) => {
			const { attributeId, required, listOrder } = action.payload;
			const targetAttribute = state.data.componentAttributes.find(
				(attr) => attr.id === attributeId
			);

			for (const formComponentAttribute of state.data.componentAttributes) {
				if (formComponentAttribute.id !== attributeId) continue;
				if (required !== undefined) formComponentAttribute.required = required;
				if (listOrder !== undefined)
					formComponentAttribute.listOrder = listOrder;
			}

			if (targetAttribute) targetAttribute.required = required;
		},

		addAttributeValue: (state, action: PayloadAction<UUID>) => {
			const targetAttribute = state.data.componentAttributes.find(
				(attr) => attr.id === action.payload
			);
			if (targetAttribute) {
				const newAttributeValue: FormComponentAttributeValue = {
					id: crypto.randomUUID(),
					attributeValue: undefined,
					productModifiers: [],
					relations: [],
					priceRule: {
						isActive: false,
						amount: 0,
						type: PriceRuleType.FIXED,
					},
				};
				targetAttribute.componentAttributeValues.push(newAttributeValue);
			}
		},
		removeAttributeValue: (
			state,
			action: PayloadAction<{ attributeId: UUID; valueId: UUID }>
		) => {
			const { attributeId, valueId } = action.payload;
			const targetAttribute = state.data.componentAttributes.find(
				(attr) => attr.id === attributeId
			);
			if (targetAttribute) {
				targetAttribute.componentAttributeValues =
					targetAttribute.componentAttributeValues.filter(
						(val) => val.id !== valueId
					);
			}
		},
		updateAttributeValue: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId: UUID;
				attributeValue: Partial<AttributeValue> | undefined;
			}>
		) => {
			const targetValue = findAttributeValue(
				state,
				action.payload.attributeId,
				action.payload.valueId
			);
			if (targetValue) {
				targetValue.attributeValue = action.payload.attributeValue;
			}
		},
		updatePriceRule: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId: UUID;
				changes: Partial<ComponentPriceRule> | null;
			}>
		) => {
			const { attributeId, valueId, changes } = action.payload;
			const targetValue = findAttributeValue(state, attributeId, valueId);
			if (targetValue && targetValue.priceRule) {
				Object.assign(targetValue.priceRule, changes);
			}
		},
		addProductModifier: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId: UUID;
				modifier: ProductModifier;
			}>
		) => {
			const targetValue = findAttributeValue(
				state,
				action.payload.attributeId,
				action.payload.valueId
			);
			if (targetValue) {
				targetValue.productModifiers.push(action.payload.modifier);
			}
		},
		updateProductModifier: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId: UUID;
				modifierIndex: number;
				changes: Partial<ProductModifier>;
			}>
		) => {
			const { attributeId, valueId, modifierIndex, changes } = action.payload;
			const targetValue = findAttributeValue(state, attributeId, valueId);
			if (targetValue?.productModifiers?.[modifierIndex]) {
				Object.assign(targetValue.productModifiers[modifierIndex], changes);
			}
		},
		removeProductModifier: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId: UUID;
				modifierIndex: number;
			}>
		) => {
			const { attributeId, valueId, modifierIndex } = action.payload;
			const targetValue = findAttributeValue(state, attributeId, valueId);
			if (targetValue?.productModifiers) {
				targetValue.productModifiers.splice(modifierIndex, 1);
			}
		},
		addRelation: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId?: UUID;
				relation: ComponentRelation;
			}>
		) => {
			const { attributeId, valueId, relation } = action.payload;
			if (valueId) {
				const targetValue = findAttributeValue(state, attributeId, valueId);
				if (targetValue) targetValue.relations.push(relation);
			} else {
				const targetAttribute = state.data.componentAttributes.find(
					(attr) => attr.id === attributeId
				);
				if (targetAttribute) targetAttribute.relations.push(relation);
			}
		},
		removeRelation: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId?: UUID;
				relationIndex: number;
			}>
		) => {
			const { attributeId, valueId, relationIndex } = action.payload;
			if (valueId) {
				const targetValue = findAttributeValue(state, attributeId, valueId);
				if (targetValue) targetValue.relations.splice(relationIndex, 1);
			} else {
				const targetAttribute = state.data.componentAttributes.find(
					(attr) => attr.id === attributeId
				);
				if (targetAttribute) targetAttribute.relations.splice(relationIndex, 1);
			}
		},
		updateRelation: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId?: UUID;
				relationIndex: number;
				changes: Partial<ComponentRelation>;
			}>
		) => {
			const { attributeId, valueId, relationIndex, changes } = action.payload;
			if (valueId) {
				const targetValue = findAttributeValue(state, attributeId, valueId);
				if (targetValue?.relations?.[relationIndex]) {
					Object.assign(targetValue.relations[relationIndex], changes);
				}
			} else {
				const targetAttribute = state.data.componentAttributes.find(
					(attr) => attr.id === attributeId
				);
				if (targetAttribute?.relations?.[relationIndex]) {
					Object.assign(targetAttribute.relations[relationIndex], changes);
				}
			}
		},
		addRelationCondition: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId?: UUID;
				relationIndex: number;
				condition: ComponentRelationCondition;
			}>
		) => {
			const { attributeId, valueId, relationIndex, condition } = action.payload;
			let relation: ComponentRelation | undefined;
			if (valueId) {
				relation = findAttributeValue(state, attributeId, valueId)?.relations?.[
					relationIndex
				];
			} else {
				relation = state.data.componentAttributes.find(
					(attr) => attr.id === attributeId
				)?.relations?.[relationIndex];
			}
			if (relation) {
				if (!relation.conditions) relation.conditions = [];
				relation.conditions.push(condition);
			}
		},
		updateRelationCondition: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId?: UUID;
				relationIndex: number;
				conditionIndex: number;
				changes: Partial<ComponentRelationCondition>;
			}>
		) => {
			const { attributeId, valueId, relationIndex, conditionIndex, changes } =
				action.payload;
			let relation: ComponentRelation | undefined;
			if (valueId) {
				relation = findAttributeValue(state, attributeId, valueId)?.relations?.[
					relationIndex
				];
			} else {
				relation = state.data.componentAttributes.find(
					(attr) => attr.id === attributeId
				)?.relations?.[relationIndex];
			}
			if (relation?.conditions?.[conditionIndex]) {
				Object.assign(relation.conditions[conditionIndex], changes);
			}
		},
		removeRelationCondition: (
			state,
			action: PayloadAction<{
				attributeId: UUID;
				valueId?: UUID;
				relationIndex: number;
				conditionIndex: number;
			}>
		) => {
			const { attributeId, valueId, relationIndex, conditionIndex } =
				action.payload;
			let relation: ComponentRelation | undefined;
			if (valueId) {
				relation = findAttributeValue(state, attributeId, valueId)?.relations?.[
					relationIndex
				];
			} else {
				relation = state.data.componentAttributes.find(
					(attr) => attr.id === attributeId
				)?.relations?.[relationIndex];
			}
			if (relation?.conditions) {
				relation.conditions.splice(conditionIndex, 1);
			}
		},
	},
});

export const componentFormActions = componentFormSlice.actions;

export const selectFormData = (state: RootState) =>
	state[StoreKeys.componentForm].data;
export const selectComponentName = (state: RootState) =>
	state[StoreKeys.componentForm].data.name;
export const selectComponentDisplayName = (state: RootState) =>
	state[StoreKeys.componentForm].data.displayName;
export const selectAttributeIds = (state: RootState) =>
	state[StoreKeys.componentForm].data.componentAttributes.map(
		(attr) => attr.id
	);

export const selectAttributeById = (state: RootState, attributeId: UUID) =>
	state[StoreKeys.componentForm].data.componentAttributes.find(
		(attr) => attr.id === attributeId
	);
export const selectAttributeAttribute = (state: RootState, attributeId: UUID) =>
	selectAttributeById(state, attributeId)?.attribute;

export const selectAttributeValueIds = (state: RootState, attributeId: UUID) =>
	selectAttributeById(state, attributeId)?.componentAttributeValues.map(
		(val) => val.id
	) || [];
export const selectAttributeValueById = (
	state: RootState,
	attributeId: UUID,
	valueId: UUID
) =>
	selectAttributeById(state, attributeId)?.componentAttributeValues.find(
		(val) => val.id === valueId
	);
export const selectAttributeValueAttributeValue = (
	state: RootState,
	attributeId: UUID,
	valueId: UUID
) => selectAttributeValueById(state, attributeId, valueId)?.attributeValue;

export const selectAttributeValuePriceRule = (
	state: RootState,
	attributeId: UUID,
	valueId: UUID
) => selectAttributeValueById(state, attributeId, valueId)?.priceRule || null;

export const selectProductModifierIds = (
	state: RootState,
	attributeId: UUID,
	valueId: UUID
) =>
	selectAttributeValueById(state, attributeId, valueId)?.productModifiers.map(
		(_, index) => index
	) || [];
export const selectProductModifierByIndex = (
	state: RootState,
	attributeId: UUID,
	valueId: UUID,
	modifierIndex: number
) =>
	selectAttributeValueById(state, attributeId, valueId)?.productModifiers?.[
		modifierIndex
	];

export const selectRelationByIndex = (
	state: RootState,
	attributeId: UUID,
	valueId: UUID | undefined,
	relationIndex: number
) => {
	if (valueId) {
		return selectAttributeValueById(state, attributeId, valueId)?.relations?.[
			relationIndex
		];
	}
	return selectAttributeById(state, attributeId)?.relations?.[relationIndex];
};
export const selectConditionByIndex = (
	state: RootState,
	attributeId: UUID,
	valueId: UUID | undefined,
	relationIndex: number,
	conditionIndex: number
) => {
	const relation = selectRelationByIndex(
		state,
		attributeId,
		valueId,
		relationIndex
	);
	return relation?.conditions?.[conditionIndex];
};

export default componentFormSlice.reducer;
